import { useEffect, useRef, useState } from 'react'
import {
  getDoc,
  onSnapshot,
  type DocumentReference,
  type DocumentSnapshot,
  type Query,
} from 'firebase/firestore'

/* The three data hooks mirroring Flutter's builders:
 *   useDocOnce          <- FutureBuilder(getDocumentOnce)   [level 1: alarm]
 *   useDocStream        <- StreamBuilder(getDocument)       [level 2: alert]
 *   useCollectionStream <- StreamBuilder(queryCollection)   [level 3: list]
 *
 * `ref/q === null` disables the hook — it never subscribes. This is how "no
 * listener before auth resolves" is enforced: pass null until initialized.
 *
 * Loading == Flutter's `!snapshot.hasData`: true until the FIRST emission.
 * A re-enabled hook (null -> ref) starts loading again.
 *
 * safeGet: a doc whose parse throws is logged and dropped (queryCollection's
 * `.where((d) => d != null)`) — a list can have fewer than `limit` items.
 * For single-doc hooks a parse failure leaves `data` null (Dart would throw
 * during build; we surface the log instead of a white screen). */

interface DocState<T> {
  data: T | null
  loading: boolean
}

export function useDocOnce<T>(
  ref: DocumentReference | null,
  parse: (snap: DocumentSnapshot) => T,
): DocState<T> {
  const [state, setState] = useState<DocState<T>>({ data: null, loading: true })
  const path = ref?.path ?? null
  const parseRef = useRef(parse)
  parseRef.current = parse

  useEffect(() => {
    if (!ref) return
    let alive = true
    setState({ data: null, loading: true })
    getDoc(ref)
      .then((snap) => {
        if (!alive) return
        try {
          setState({ data: parseRef.current(snap), loading: false })
        } catch (e) {
          console.error(`safeGet: failed to parse ${snap.ref.path}`, e)
          setState({ data: null, loading: false })
        }
      })
      .catch((e) => {
        console.error(`useDocOnce: get failed for ${path}`, e)
        if (alive) setState({ data: null, loading: false })
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  return ref ? state : { data: null, loading: true }
}

export function useDocStream<T>(
  ref: DocumentReference | null,
  parse: (snap: DocumentSnapshot) => T,
): DocState<T> {
  const [state, setState] = useState<DocState<T>>({ data: null, loading: true })
  const path = ref?.path ?? null
  const parseRef = useRef(parse)
  parseRef.current = parse

  useEffect(() => {
    if (!ref) return
    setState({ data: null, loading: true })
    const unsub = onSnapshot(
      ref,
      (snap) => {
        try {
          setState({ data: parseRef.current(snap), loading: false })
        } catch (e) {
          console.error(`safeGet: failed to parse ${snap.ref.path}`, e)
          setState({ data: null, loading: false })
        }
      },
      (e) => {
        console.error(`useDocStream: ${path}`, e)
        setState({ data: null, loading: false })
      },
    )
    return unsub
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  return ref ? state : { data: null, loading: true }
}

interface ListState<T> {
  data: T[] | null
  loading: boolean
}

export function useCollectionStream<T>(
  q: Query | null,
  parse: (snap: DocumentSnapshot) => T,
  /** Stable key for resubscription — Query has no .path; callers pass one. */
  key: string | null,
): ListState<T> {
  const [state, setState] = useState<ListState<T>>({ data: null, loading: true })
  const parseRef = useRef(parse)
  parseRef.current = parse
  const qRef = useRef(q)
  qRef.current = q

  useEffect(() => {
    const query = qRef.current
    if (!query) return
    setState({ data: null, loading: true })
    const unsub = onSnapshot(
      query,
      (snap) => {
        const items: T[] = []
        for (const doc of snap.docs) {
          try {
            items.push(parseRef.current(doc)) // safeGet
          } catch (e) {
            console.error(`safeGet: dropping ${doc.ref.path}`, e)
          }
        }
        setState({ data: items, loading: false })
      },
      (e) => {
        console.error(`useCollectionStream: ${key}`, e)
        setState({ data: null, loading: false })
      },
    )
    return unsub
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return qRef.current ? state : { data: null, loading: true }
}
